import { Link } from "react-router-dom";

const SubscriptionPolicy = () => {
  return (
    <section className="terms-wrapper">
      <div className="terms-container">
        <h1 className="terms-title">PawPlate Subscription Policy</h1>
        <p className="terms-intro">Last updated: 2025-07-28</p>

        <div className="">
          <p>
            Opami, LLC (“Opami,” “we,” “us”, “Company”) operates any
            downloadable software, mobile applications, and the Company’s
            products and services offered at{" "}
            <a href="https://pawplate.app">https://pawplate.app</a> and{" "}
            <a href="https://pawplate.io">https://pawplate.io</a> (collectively,
            the “Service”). This Subscription Policy explains the terms
            governing subscriptions to our Service. By subscribing or renewing a
            subscription, you agree to be bound by this Subscription Policy and
            by Opami’s <a href="/terms">Terms of Use</a> and{" "}
            <Link to="">Privacy Policy</Link>.
          </p>

          <p>
            If you do not agree to all of these terms, do not subscribe or use
            the Service.
          </p>

          <h3 className="terms-heading">Subscription Plans and Pricing</h3>
          <p>
            We offer several recurring subscription plans billed on a 1-week,
            4-week, or 12-week basis. From time to time, we may offer a one-time
            discounted introductory price (the “Discount Price”) to first-time
            subscribers. When you select a subscription plan with such a
            promotion, the initial charge will be at the Discount Price.
          </p>

          <table border="1" cellpadding="8" cellspacing="0">
            <thead>
              <tr>
                <th>Plan Duration</th>
                <th>Full Price (USD)</th>
                <th>Introductory Discount Price (USD)</th>
                <th>Price per Day – Full / Discount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1 Week Trial</td>
                <td>$17.77</td>
                <td>$6.93</td>
                <td>$2.54 / $0.99</td>
              </tr>
              <tr>
                <td>4 Week</td>
                <td>$38.95</td>
                <td>$15.19</td>
                <td>$1.39 / $0.54</td>
              </tr>
              <tr>
                <td>12 Week</td>
                <td>$94.85</td>
                <td>$36.99</td>
                <td>$1.13 / $0.44</td>
              </tr>
            </tbody>
          </table>

          <p>
            <strong>Automatic Rebilling at Full Price:</strong> Unless
            explicitly stated otherwise at checkout{" "}
            <b>
              future renewals after the initial period will be charged at the
              standard Full Price
            </b>
            , corresponding to your plan. You will be notified of the pricing
            before the first charge, and renewal terms will be presented clearly
            before you confirm. For weekly subscriptions, the initial period may
            serve as a trial and will automatically convert to a monthly plan at
            the applicable full rate unless canceled.
          </p>

          <p>
            <strong>Super Discount Offers:</strong> From time to time, in our
            own direction, and in accordance with applicable consumer protection
            laws, Opami may present users with promotional pricing incentives.
            Any such time-limited promotional pricing - referred to herein as a
            “Super Discount” - will be clearly displayed prior to purchase and
            will apply exclusively to the initial billing term of the selected
            subscription. These promotional prices may be made available only
            during the active session in which they are presented and are not
            guaranteed for future availability.
          </p>
          <p>
            Unless expressly stated otherwise at the time of offer, the
            subscription will renew at the then-current standard rate applicable
            to the selected plan. Opami reserves the right to discontinue or
            modify promotional pricing at any time, provided that any changes
            shall not apply retroactively to subscriptions already purchased
          </p>

          <p>
            <strong>Plan Terms:</strong>
          </p>
          <ul>
            <li>
              All subscription fees are billed in full at the start of each
              term.{" "}
            </li>
            <li>
              Promotions are valid for one-time use and non-transferable.{" "}
            </li>
            <li>
              You will always be shown the total price, applicable discounts,
              and renewal price before confirming your purchase.
            </li>
          </ul>
          <p>
            We reserve the right to update our plans, pricing, and promotional
            structure at any time, provided that such changes will not affect
            current subscriptions until the next renewal cycle.
          </p>

          <p>
            <strong>Pricing:</strong> Subscription prices are set in U.S.
            dollars (USD) and are displayed at the time of purchase. If you
            purchase in the App Store or Google Play, the price may be shown in
            your local currency as determined by the platform. All fees are
            charged in advance on a recurring basis according to the billing
            cycle you select. We reserve the right to modify prices for the
            Service at any time; any change will only apply to future billing
            periods after you have been notified.
          </p>
          <p>
            <strong>Promotions:</strong> We may offer free trials, discounts,
            promotional bundles, or referral credits from time to time. These
            offers may be limited. For example, a free trial gives you access at
            no cost for a defined period; a discount may reduce your price for a
            limited time; a referral credit may apply a one-time credit to your
            subscription.{" "}
            <b>
              All promotional and referral programs are subject to terms
              specified at the time of the offer. Referral credits are
              non-transferable and cannot be redeemed for cash.
            </b>
          </p>

          <h3 className="terms-heading">Billing and Automatic Renewal</h3>
          <p>
            <strong>Auto-Renewal:</strong> Your subscription will automatically
            renew at the end of each billing period unless you cancel before the
            renewal date. Auto-renewal ensures uninterrupted access to the
            Service.
          </p>
          <p>
            <strong>Renewal Charges:</strong> At each renewal, the subscription
            fee will be charged to your selected payment method at the
            then-current rate for your plan. You may be charged no more than
            once per billing period.
          </p>
          <p>
            <strong>Price Changes:</strong> Opami may change subscription fees
            or introduce new fees. Any price increase will take effect at the
            start of the next billing period after we provide you notice. You
            may cancel your subscription before the end of the current period if
            you do not agree to a price increase; otherwise the new price will
            apply to the next charge.
          </p>
          <p>
            <strong>Plan Changes:</strong> You may upgrade or downgrade your
            subscription plan, but only at the start of a new billing period.
            Mid-cycle upgrades or downgrades are not supported; any change in
            plan will take effect upon your next renewal.
          </p>

          <h3 className="terms-heading">
            Managing Subscriptions and Cancellation
          </h3>
          <p>
            <strong>Managing Subscriptions: </strong> You may cancel your
            subscription at any time prior to the renewal date to avoid future
            charges. Cancellation terms vary depending on your method of
            purchase:
          </p>
          <ul className="terms-list">
            <li>
              <b>App Store (iOS) or Google Play (Android) Subscriptions:</b> If
              you subscribed via Apple or Google Play, you must cancel through
              your respective App Store account settings. Due to platform
              limitations, Opami cannot cancel these subscriptions on your
              behalf.
            </li>
            <li>
              For iOS: Open your device’s Settings → tap your name →
              Subscriptions → select PawPlate and tap Cancel Subscription.
            </li>
            <li>
              For Android: Open Google Play Store → tap your profile icon →
              Payments & Subscriptions → Subscriptions → select PawPlate and
              cancel.
            </li>
          </ul>
          <p>
            Cancellation will be effective at the end of your current billing
            period. You will retain access to the Service until the subscription
            expires. If you fail to cancel via the correct method before your
            renewal date, we cannot issue a refund for the renewed period.
          </p>

          <p>
            <strong>Web-Based Subscriptions (Stripe, Direct Checkout): </strong>{" "}
            If you subscribed via our website using a credit card or Stripe, you
            may cancel by:
            <spam>
              {" "}
              Email us at{" "}
              <a href="mailto:support@pawplate.io">support@pawplate.io</a> with
              the subject line “Subscription Cancellation”, from the email
              address associated with your account.
            </spam>
          </p>
          <p>
            Upon receiving a valid request, we will cancel your subscription and
            confirm by email.{" "}
            <b>
              To be effective before your next billing date, your request must
              be received at least 24 hours prior to the renewal time.
            </b>
          </p>
          <p>
            We reserve the right to verify your identity before processing
            cancellations. Refunds will be handled in accordance with our Refund
            Policy.
          </p>

          <p>
            <strong>No Proration or Refund on Cancellation</strong> We do not
            offer prorated refunds for unused time in a billing period. If you
            cancel mid-cycle, your subscription benefits will simply expire at
            the end of the paid period and you will not be billed thereafter. No
            refund will be issued for the unused portion of the period unless
            required by law or determined by us at our sole discretion.
          </p>

          <p>
            <strong>Failed Payments:</strong> If a scheduled renewal payment
            fails (e.g., due to an expired credit card or insufficient funds),
            we may attempt to retry the charge. If the payment cannot be
            collected within a 1-day grace period, your subscription may be
            suspended or canceled. You will retain access to the Service during
            the grace period, but if payment is not successfully processed by
            the end of the 1-day period, your access to the paid features will
            be restricted, and your subscription may be terminated. If your
            payment information has changed, you can update it through the
            payment settings in the app or your account. We will notify you via
            email or in-app message if your payment has failed and if your
            account is at risk of suspension.
          </p>

          <h3 className="terms-heading">Taxes</h3>
          <p>
            Subscription fees are exclusive of any applicable taxes, including
            but not limited to sales tax, value-added tax (VAT), goods and
            services tax (GST), or other local, state, or national taxes that
            may be imposed on digital services in your jurisdiction. Where
            required by law, Opami will calculate, collect, and remit such taxes
            at the time of purchase. The amount of tax charged (if any) will
            depend on the billing information you provide and the applicable tax
            laws in your country or region. For purchases made through mobile
            app platforms (such as the Apple App Store or Google Play), any
            applicable taxes may be included in the price displayed or added at
            checkout, in accordance with the platform's billing practices and
            regulatory obligations. Opami is not responsible for how such
            platforms handle tax collection or reporting. You are solely
            responsible for ensuring compliance with any applicable tax
            obligations in your jurisdiction not collected at the point of sale.
          </p>
          <p>
            <b>Platform Billing: </b>If you subscribe via the Apple App Store
            (iOS) or Google Play Store (Android), your billing and payment are
            handled by Apple or Google, not directly by us (with the External
            Payment Methods for U.S. iOS Users exception as described below).
            Opami does not receive or store your credit card information from
            those platforms. The subscription terms are implemented through the
            respective store’s billing system.
          </p>
          <h3 className="terms-heading">
            External Payment Methods for U.S. iOS Users{" "}
          </h3>
          <p>
            In accordance with updated regulatory and platform rules applicable
            to the United States, Opami may offer eligible users the option to
            purchase subscriptions via a third-party payment provider through an
            external link, including but not limited to Stripe. This section
            applies solely to U.S.-based users of the iOS version of the App.
            Users outside the United States or using other App Store regions are
            not eligible for external payment processing and must subscribe
            using Apple’s in-app purchase system.{" "}
          </p>
          <p>
            If eligible, users may be presented with a clearly labeled link
            within the App that directs them to a secure third-party checkout
            page (e.g., hosted by Stripe). By proceeding via this link, you
            acknowledge and agree that:
          </p>
          <ul>
            <li>
              Your subscription is processed directly by Opami’s third-party
              payment provider, not by Apple;
            </li>
            <li>
              Apple does not process, verify, or manage these payments and bears
              no responsibility for any aspect of the transaction;
            </li>
            <li>
              The checkout experience will occur outside the App environment, in
              compliance with Apple’s “External Link Entitlement” policy for
              U.S. developers.
            </li>
          </ul>
          <p>
            Opami reserves the right to modify, suspend, or discontinue the use
            of external payment methods at any time, with or without prior
            notice, including in response to changes in law, platform policies,
            or business needs.
          </p>
          <p>
            <b>Managing via App Stores:</b> Subscriptions made on iOS must be
            managed through your Apple ID as described above; subscriptions made
            on Android must be managed through your Google account. We cannot
            directly cancel App Store or Google Play subscriptions on your
            behalf.
          </p>
          <p>
            <b>Immediate Delivery; Waiver of Withdrawal Rights:</b> Per EU/UK
            law and app store requirements, when you purchase a digital
            subscription via these platforms, the content is made available
            immediately, and you agree that you forfeit any statutory withdrawal
            (cooling-off) right. This means that once your subscription starts,
            you cannot “return” or cancel it for a refund under the 14-day rule
            (unless otherwise specified by the store).
          </p>
          <h3 className="terms-heading">Refund Policy</h3>
          <p>
            <b>General Rule:</b> Subscription fees are non-refundable, except
            where required by law or at our sole discretion. We strongly
            encourage you to review the Service before subscribing, as we do not
            provide refunds simply due to a change of mind.
          </p>
          <p>
            <b>Limited Exceptions:</b> In limited circumstances, we may issue a
            refund or credit (partial or full), at our discretion, such as when
            the service has been unavailable for an extended period due to our
            fault, or a technical issue prevented you from using the Service
            despite your best efforts. Any such refund or credit is not
            guaranteed and will require you to submit a request to support. We
            will review support inquiries on a case-by-case basis.
          </p>
          <p>
            <b>Dissatisfaction:</b> If you are dissatisfied with the Service,
            please contact support (see Contact Us below). In exceptional cases
            of verified dissatisfaction or inability to use the Service, we may
            consider a refund. However, contacting support does not guarantee a
            refund; all refunds are subject to our sole discretion and
            applicable law.
          </p>
          <p>
            If you encounter an issue with billing or want a refund for a
            subscription purchased through Apple or Google, you must contact
            Apple or Google directly (with the External Payment Methods for U.S.
            iOS Users exception as described above). Apple and Google have their
            own refund policies. Opami cannot override or bypass those store
            policies. Please refer to Apple’s or Google’s support resources for
            refund requests on in-app purchases.
            <b> EU/UK Consumer Rights (14-Day Withdrawal) </b>
          </p>
          <p>
            European Union (EU) and United Kingdom (UK) consumers have a
            statutory right of withdrawal (cooling-off) for distance contracts.
            Under these rules, you generally have 14 days to cancel a
            subscription after purchase for any reason. However, by agreeing to
            immediate access, you may waive this right for digital content. In
            compliance with EU/UK law, we inform you that as soon as you start
            using the Service and access the Service immediately, you lose the
            right to cancel under the 14-day withdrawal rule. If you do not
            agree to waive this right, do not subscribe or delay the start of
            your subscription until the end of the 14-day period. We will
            clearly inform you at the point of sale about this waiver.
          </p>

          <h3 className="terms-heading">Changes to This Subscription Policy</h3>
          <p>
            We may revise this Subscription Policy from time to time. When we
            do, we will update the “Last updated” date at the top. Any
            significant changes will be posted on this page and, if appropriate,
            you may be notified through the App or by email. Your continued
            subscription and use of the Service after the changes become
            effective will constitute acceptance of the new terms. We encourage
            you to review this policy periodically to stay informed of any
            changes.
          </p>
          <p>Opami, LLC</p>
          <p>
            Email: <a href="mailto:support@pawplate.io">support@pawplate.io </a>
          </p>
          <p>Address: 1111B S Governors Ave, STE 25056, Dover, DE 19904, USA</p>

          <div></div>
        </div>
      </div>
    </section>
  );
};

export default SubscriptionPolicy;
